/**
 * accounting · o catálogo de relatórios (spec 03 §11 · tela 8.7).
 *
 * O catálogo é **código versionado, não cadastro**: não existe tela que crie ou
 * edite um relatório, e `GET /relatorios` só o FILTRA por permissão (DS-28). Por
 * isso o identificador de cada card é um enum, e não texto livre — acrescentar um
 * relatório é editar este arquivo, e esquecer de dar destino a ele quebra o build
 * da web, onde o mapa de rotas é exaustivo sobre este mesmo enum.
 *
 * O que NÃO está aqui, de propósito:
 *  - **rota de destino**: a tela mapeia `id → rota` (o mesmo desenho da busca
 *    global, que devolve `tipo` e deixa o destino com quem tem o roteador tipado);
 *  - **frequência**: o protótipo tinha badges "Diário/Semanal/Mensal" que prometiam
 *    um agendador que não existe no v1 (spec 8.7 §Migração);
 *  - **card "Em breve"**: `funil` saiu do catálogo — recurso sem tela nem endpoint
 *    especificado. Card que não leva a lugar nenhum é promessa, não relatório.
 */
import { z } from 'zod'
import { meta } from './comum.js'

/** Os oito domínios do catálogo, na ordem em que a tela os empilha. */
export const dominioDeRelatorio = z.enum([
  'comercial',
  'receita',
  'ops',
  'desp',
  'fin',
  'rh',
  'prod',
  'aud',
])
export type DominioDeRelatorio = z.infer<typeof dominioDeRelatorio>

/**
 * Os 29 relatórios do catálogo (spec 8.7 §Catálogo).
 *
 * `funil` NÃO está na lista: era o card "Em breve" do protótipo, e a spec o retirou
 * do v1. O histórico da DS-17 continua sendo gravado para viabilizá-lo depois.
 */
export const idDeRelatorio = z.enum([
  'previsao',
  'metas',
  'pipeline',
  'mrr',
  'churn',
  'fatnf',
  'aging',
  'comis',
  'basq',
  'mgcamp',
  'custoforn',
  'cap',
  'vargasto',
  'forn',
  'dre',
  'rent',
  'varorc',
  'fluxo',
  'saldo',
  'concil',
  'pagrh',
  'headcount',
  'tarefas',
  'carga',
  'trilha',
  'perm',
  'regras',
  'log',
  'jobs',
])
export type IdDeRelatorio = z.infer<typeof idDeRelatorio>

/** Preferência pessoal da 8.7; a transformação conserva ordem e elimina repetição. */
export const entradaDeFavoritosDeRelatorios = z.object({
  favoritosRelatorios: z.array(idDeRelatorio).max(29).transform((ids) => [...new Set(ids)]),
})
export type EntradaDeFavoritosDeRelatorios = z.infer<typeof entradaDeFavoritosDeRelatorios>

/** Os dois painéis próprios de 8.7 — o valor é o que vai em `?relatorio=`. */
export const painelDeRelatorio = z.enum(['log-alteracoes', 'jobs'])
export type PainelDeRelatorio = z.infer<typeof painelDeRelatorio>

export const cartaoDeRelatorio = z.object({
  id: idDeRelatorio,
  dominio: dominioDeRelatorio,
  nome: z.string(),
  descricao: z.string(),
  /** Nome Lucide do ícone do tile — o mesmo do protótipo. */
  icone: z.string(),
  /**
   * `null` = o card navega para uma tela de destino; caso contrário, abre um painel
   * DENTRO da 8.7 (`?relatorio=<valor>`), que é onde a auditoria e a fila de mortos
   * moram — a DS-10 é explícita em não inventar tela nova para a trilha.
   */
  painel: painelDeRelatorio.nullable(),
})
export type CartaoDeRelatorio = z.infer<typeof cartaoDeRelatorio>

/**
 * `meta.favoritos` sai de `usuario_interno.preferencias.favoritosRelatorios` (spec
 * 02 §1.7) e vem JÁ RECORTADO aos cards visíveis: favoritar não é um jeito de
 * contornar permissão, e um favorito de um relatório que a pessoa perdeu o direito
 * de ver não pode reaparecer na seção do topo.
 */
export const respostaDoCatalogo = z.object({
  data: z.array(cartaoDeRelatorio),
  meta: meta.extend({ favoritos: z.array(idDeRelatorio) }),
})
export type RespostaDoCatalogo = z.infer<typeof respostaDoCatalogo>
